export const LIVE_ACTIVITY_SETTING_MODULES = Object.freeze({
  PURCHASES: "purchases",
  SERVICES: "services",
  OCCURRENCES: "occurrences",
  VETERINARY: "veterinary",
});

const LIVE_ACTIVITY_MODULES = LIVE_ACTIVITY_SETTING_MODULES;

export const LIVE_ACTIVITY_SETTINGS_KEY = "fz:liveActivities:settings:v1";

export const LIVE_ACTIVITY_MODULE_META = Object.freeze({
  [LIVE_ACTIVITY_MODULES.PURCHASES]: {
    label: "Compras",
    description: "Criação de pedidos e mudanças de status.",
  },
  [LIVE_ACTIVITY_MODULES.SERVICES]: {
    label: "Serviços",
    description: "Criação e mudanças de status de serviços internos e terceirizados.",
  },
  [LIVE_ACTIVITY_MODULES.OCCURRENCES]: {
    label: "Ocorrências",
    description: "Criação e mudanças de status de ocorrências.",
  },
  [LIVE_ACTIVITY_MODULES.VETERINARY]: {
    label: "Veterinário",
    description: "Criação de atendimentos veterinários e mudanças de status.",
  },
});

const DEFAULT_MODULES = Object.freeze(Object.fromEntries(
  Object.values(LIVE_ACTIVITY_MODULES).map(moduleKey => [moduleKey, true])
));

export const LIVE_ACTIVITY_MIN_SIMULTANEOUS = 1;
export const LIVE_ACTIVITY_MAX_SIMULTANEOUS = 7;
export const LIVE_ACTIVITY_DEFAULT_SIMULTANEOUS = 7;
export const LIVE_ACTIVITY_FINAL_LIFETIME_HOUR_OPTIONS = Object.freeze([1, 2, 3, 4, 5, 6, 7, 8]);
export const LIVE_ACTIVITY_DEFAULT_FINAL_LIFETIME_HOURS = 1;
export const LIVE_ACTIVITY_MIN_FINAL_LIFETIME_HOURS = 1;
export const LIVE_ACTIVITY_MAX_FINAL_LIFETIME_HOURS = 8;

function normalizeFinalLifetimeHours(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return LIVE_ACTIVITY_DEFAULT_FINAL_LIFETIME_HOURS;
  return Math.max(LIVE_ACTIVITY_MIN_FINAL_LIFETIME_HOURS, Math.min(LIVE_ACTIVITY_MAX_FINAL_LIFETIME_HOURS, Math.trunc(n)));
}

function normalizeMaxSimultaneousActivities(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return LIVE_ACTIVITY_DEFAULT_SIMULTANEOUS;
  return Math.max(LIVE_ACTIVITY_MIN_SIMULTANEOUS, Math.min(LIVE_ACTIVITY_MAX_SIMULTANEOUS, Math.trunc(n)));
}

export const LIVE_ACTIVITY_SETTINGS_DEFAULT = Object.freeze({
  enabled: true,
  modules: DEFAULT_MODULES,
  maxSimultaneousActivities: LIVE_ACTIVITY_DEFAULT_SIMULTANEOUS,
  // Tempo de permanência após etapa final, em horas.
  // A vida operacional de uma instância de Live é tratada separadamente pelo servidor.
  finalLifetimeHours: LIVE_ACTIVITY_DEFAULT_FINAL_LIFETIME_HOURS,
  updatedAt: "",
  updatedBy: "",
});

export function normalizeLiveActivitySettings(input = null) {
  const base = input && typeof input === "object" ? input : {};
  const rawModules = base.modules && typeof base.modules === "object" ? base.modules : {};
  const modules = Object.fromEntries(Object.values(LIVE_ACTIVITY_MODULES).map(moduleKey => [
    moduleKey,
    rawModules[moduleKey] !== false,
  ]));
  return {
    ...LIVE_ACTIVITY_SETTINGS_DEFAULT,
    ...base,
    enabled: base.enabled !== false,
    modules,
    maxSimultaneousActivities: normalizeMaxSimultaneousActivities(base.maxSimultaneousActivities ?? base.max_simultaneous_activities),
    finalLifetimeHours: normalizeFinalLifetimeHours(
      base.finalLifetimeHours ??
      base.final_lifetime_hours ??
      base.finalDismissalHours ??
      base.final_dismissal_hours ??
      // v2.221.07: compatibilidade com configs antigas. O valor antigo era exibido
      // como dias por engano; a partir de agora o mesmo número representa horas.
      base.finalLifetimeDays ??
      base.final_lifetime_days ??
      base.finalDismissalDays ??
      base.final_dismissal_days
    ),
    updatedAt: String(base.updatedAt || base.updated_at || ""),
    updatedBy: String(base.updatedBy || base.updated_by || ""),
  };
}

export function cacheLiveActivitySettings(settings) {
  const normalized = normalizeLiveActivitySettings(settings);
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem(LIVE_ACTIVITY_SETTINGS_KEY, JSON.stringify(normalized));
      window.dispatchEvent(new CustomEvent("fz:liveActivitySettingsChanged", { detail: normalized }));
    }
  } catch (_error) {}
  return normalized;
}

export function readCachedLiveActivitySettings() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return normalizeLiveActivitySettings(null);
    const parsed = JSON.parse(window.localStorage.getItem(LIVE_ACTIVITY_SETTINGS_KEY) || "null");
    return normalizeLiveActivitySettings(parsed);
  } catch (_error) {
    return normalizeLiveActivitySettings(null);
  }
}


export function initLiveActivitySettingsCache() {
  try {
    if (typeof window === "undefined" || !window.localStorage) {
      return normalizeLiveActivitySettings(null);
    }
    const raw = window.localStorage.getItem(LIVE_ACTIVITY_SETTINGS_KEY);
    if (!raw) {
      return normalizeLiveActivitySettings(null);
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return normalizeLiveActivitySettings(null);
    }
    const hasRealValue =
      (parsed.updatedAt && String(parsed.updatedAt).trim() !== "") ||
      (parsed.modules &&
        typeof parsed.modules === "object" &&
        Object.values(parsed.modules).some((value) => value === false));
    if (!hasRealValue) {
      return normalizeLiveActivitySettings(null);
    }
    return normalizeLiveActivitySettings(parsed);
  } catch (_error) {
    return normalizeLiveActivitySettings(null);
  }
}

export function isLiveActivityModuleEnabled(settingsLike, moduleKey) {
  const settings = normalizeLiveActivitySettings(settingsLike);
  const key = String(moduleKey || "").trim().toLowerCase();
  if (!settings.enabled) return false;
  if (!key) return true;
  return settings.modules?.[key] !== false;
}
